import { useMemo, useReducer } from "react";
import { useFormDynamicFields } from "../../hooks/useForm/useFormDynamicFields";
import type { FormStore } from "../../store/types";
import type { AttributeCollectionId, AttributeGroupId } from "../../types";

/**
 * グループの操作に関するフック
 * グループの表示、追加、削除に関連するロジックを提供します
 *
 * @note: このコンポーネントでは2つの状態管理アプローチを組み合わせています
 * - zustand: フォームデータの永続的な状態管理（ストア）
 * - useReducer: UIの再レンダリングをトリガーするためのローカル状態
 */
export function useGroupOperations({
  collectionId,
  groupId,
  groupIndex,
  store,
}: {
  collectionId?: AttributeCollectionId;
  groupId: AttributeGroupId;
  groupIndex: number;
  store: FormStore;
}) {
  // 状態更新トリガー用のシンプルなリデューサー
  // zustandストアの更新後にUIの再レンダリングを強制するために使用
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  // 動的フィールド関連のフックを使用
  const { getDynamicFieldGroup } = useFormDynamicFields({ store });

  // 動的フィールドグループの情報を取得
  const dynamicFieldGroup = collectionId
    ? getDynamicFieldGroup(collectionId, groupId)
    : null;

  // グループデータをストアから取得
  const groupData = useMemo(() => {
    return store.getGroup(groupId);
  }, [store, groupId]);

  // 削除ボタンを表示するかどうか
  // - グループが動的であること
  // - 最初のグループ（インデックス0）でないこと
  const showRemoveButton = groupData?.isDynamic === true && groupIndex > 0;

  // 追加ボタンを表示するかどうか
  // - グループが動的であること
  // - 追加が許可されていること
  // - グループが空か、現在のグループが最後のグループであること
  const showAddButton = useMemo(() => {
    if (!dynamicFieldGroup?.canAdd || !groupData?.isDynamic) {
      return false;
    }

    const { fieldValues } = dynamicFieldGroup;

    // フィールド値が空の場合、または現在のグループが最大インデックスを持つ場合
    return (
      fieldValues.length === 0 ||
      (fieldValues.length > 0 &&
        groupIndex === Math.max(...fieldValues.map((v) => v.groupIndex)))
    );
  }, [dynamicFieldGroup, groupData?.isDynamic, groupIndex]);

  // グループを削除する関数
  const removeGroup = (idx: number) => {
    if (!collectionId || !dynamicFieldGroup) {
      return;
    }

    // 直接削除を実行（zustandストア更新）
    dynamicFieldGroup.removeGroup(idx);

    // 状態更新をトリガーする（React再レンダリング用）
    // zustandはデータ変更を検知できても、UIが常に再計算されるとは限らないため
    forceUpdate();
  };

  // グループを追加する関数
  const addGroup = () => {
    if (!collectionId || !dynamicFieldGroup) {
      return;
    }

    // グループを追加（zustandストア更新）
    dynamicFieldGroup.addGroup();

    // 状態更新をトリガーする（React再レンダリング用）
    forceUpdate();
  };

  return {
    groupData,
    showRemoveButton,
    showAddButton,
    removeGroup,
    addGroup,
  };
}
